import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { printReceiptById } from "@/modules/billing/presentation/ui/ReceiptPreviewByApiDialog";
import type { PackageTemplateDTO } from "@/modules/packages/domain/contracts";
import type { StackedPackagePeriod } from "@/modules/packages/domain/lib/stacked-package-periods";
import { usePackageTemplateQuery } from "@/modules/packages/presentation/api/queries";
import type { PrivateLessonTemplateDTO } from "@/modules/private-lessons/domain/contracts";
import { usePrivateLessonTemplateQuery } from "@/modules/private-lessons/presentation/api/queries";
import { useStudentQuery } from "@/modules/students/presentation/api/queries";
import { formatDate } from "@/shared/lib/date-time";
import { printSellPackageReceipt } from "@/shared/lib/print-sell-package-receipt";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SellPackageStackedPeriodPreview } from "@/shared/ui/SellPackageStackedPeriodPreview";
import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { differenceInCalendarDays } from "date-fns";
import {
  ArrowRightIcon,
  CalendarIcon,
  CheckCircleIcon,
  PlusIcon,
  PrinterIcon,
} from "lucide-react";

type PackageType = "package" | "private-lesson";

function parseStackedPeriods(encoded: string | undefined): StackedPackagePeriod[] {
  if (!encoded) return [];
  return encoded
    .split(";")
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => {
      const [periodIndexRaw, startDate, expireDate] = chunk.split(":");
      const periodIndex = Number.parseInt(periodIndexRaw ?? "", 10);
      if (
        !startDate ||
        !expireDate ||
        Number.isNaN(periodIndex) ||
        periodIndex < 1
      ) {
        return null;
      }
      return { periodIndex, startDate, expireDate };
    })
    .filter((p): p is StackedPackagePeriod => p != null)
    .sort((a, b) => a.periodIndex - b.periodIndex);
}

function parseYmdLocal(ymd: string): Date | null {
  const parts = ymd.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (y == null || m == null || d == null) return null;
  return new Date(y, m - 1, d);
}

function SellPackageSuccessPage() {
  const nav = useNavigate({ from: "/app/sell/success" });
  const search = useSearch({
    from: "/app/sell/success",
  });

  const partnerMode = search.type === "partner-off-site";

  const isPrivateLessonSell =
    search.type === "private-lesson" ||
    search.type === "private-lesson-group" ||
    search.type === "private-lesson-shared";

  const packageType: PackageType = partnerMode
    ? "package"
    : isPrivateLessonSell
      ? "private-lesson"
      : "package";

  // Query student and template details
  const { data: student } = useStudentQuery(
    partnerMode ? "" : (search.studentId ?? ""),
  );
  const { data: packageTemplate } = usePackageTemplateQuery(
    !partnerMode && packageType === "package" ? (search.templateId ?? "") : "",
  );
  const { data: privateLessonTemplate } = usePrivateLessonTemplateQuery(
    !partnerMode && isPrivateLessonSell ? (search.templateId ?? "") : "",
  );

  const template =
    packageType === "package" ? packageTemplate : privateLessonTemplate;

  if (partnerMode) {
    const totalPartner =
      search.finalPrice &&
        Number.parseFloat(search.finalPrice) > 0 &&
        Number.isFinite(Number.parseFloat(search.finalPrice))
        ? Number.parseFloat(search.finalPrice)
        : 0;
    const originalPartner =
      search.originalPrice &&
        Number.parseFloat(search.originalPrice) > 0 &&
        Number.isFinite(Number.parseFloat(search.originalPrice))
        ? Number.parseFloat(search.originalPrice)
        : null;
    const discountPartnerAmt =
      search.discountAmount != null &&
        search.discountAmount !== "" &&
        Number.parseFloat(search.discountAmount) > 0
        ? Number.parseFloat(search.discountAmount)
        : null;

    const hasCreditInvoicePartner = Boolean(search.creditInvoiceId);
    const creditPaidPartner =
      search.creditPaid != null && search.creditPaid !== ""
        ? Number.parseInt(search.creditPaid, 10)
        : null;
    const creditRemainPartner =
      search.creditRemaining != null && search.creditRemaining !== ""
        ? Number.parseInt(search.creditRemaining, 10)
        : null;
    const creditSummaryPartner =
      hasCreditInvoicePartner &&
      creditPaidPartner != null &&
      !Number.isNaN(creditPaidPartner) &&
      creditRemainPartner != null &&
      !Number.isNaN(creditRemainPartner);
    const partnerClassNames =
      search.partnerClassLabels
        ?.split("|")
        .map((s) => s.trim())
        .filter(Boolean) ?? [];
    const partnerInstrNames =
      search.partnerInstructorLabels
        ?.split("|")
        .map((s) => s.trim())
        .filter(Boolean) ?? [];

    const partnerValidDaysFromSale =
      search.startDate && search.expireDate
        ? (() => {
          const end = parseYmdLocal(search.expireDate);
          const start = parseYmdLocal(search.startDate);
          if (!end || !start) return null;
          const d = differenceInCalendarDays(end, start);
          return Number.isFinite(d) && d >= 0 ? d : null;
        })()
        : null;

    const partnerSessionsScheduled =
      search.partnerSessionsScheduled != null &&
        search.partnerSessionsScheduled !== ""
        ? Number.parseInt(search.partnerSessionsScheduled, 10)
        : null;
    const sessionsScheduledLegit =
      partnerSessionsScheduled != null &&
      Number.isFinite(partnerSessionsScheduled) &&
      partnerSessionsScheduled >= 0;

    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: "ຂາຍແພັກເກດ", to: "/app/sell" },
              { label: "ສຳເລັດ" },
            ]}
          />
        </Header>
        <Main>
          <div className="mx-auto max-w-2xl">
            <div className="rounded-xl border bg-card p-8 shadow-lg">
              <div className="mb-6 flex flex-col items-center">
                <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                  <CheckCircleIcon className="size-8 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="mb-2 font-bold text-2xl">
                  ບັນທຶກການຂາຍ partner ສຳເລັດ
                </h2>
                <p className="text-center text-muted-foreground text-sm">
                  ບັນທຶກລາຍການຕາມໂຄງການ · ບໍ່ເຊື່ອມນັກຮຽນ ຫຼື ແພັກເກດ
                </p>
              </div>
              <div className="mb-6 space-y-5 border-t pt-6">
                <div>
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                    ID ການຂາຍ
                  </p>
                  <p className="break-all font-mono text-sm">
                    {search.partnerSaleId ?? "—"}
                  </p>
                </div>
                {search.partnerLabel ? (
                  <div>
                    <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                      ຊື່ partner / ບ່ອນຈັດ
                    </p>
                    <p className="font-semibold">{search.partnerLabel}</p>
                  </div>
                ) : null}
                {search.partnerOffSiteDistance ? (
                  <div>
                    <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                      ໄລຍະນອກສະຖານທີ່
                    </p>
                    <p className="font-semibold">
                      {search.partnerOffSiteDistance === "far"
                        ? "ນອກສະຖານທີ່ໄກ"
                        : "ນອກສະຖານທີ່ໃກ້"}
                    </p>
                  </div>
                ) : null}
                {search.partnerInstallmentCount &&
                search.settlementMode === "credit" ? (
                  <div>
                    <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                      ແຜນງວດຊຳລະ
                    </p>
                    <p className="font-semibold">
                      ແບ່ງ {search.partnerInstallmentCount} ງວດ
                    </p>
                  </div>
                ) : null}
                {partnerClassNames.length > 0 ? (
                  <div>
                    <p className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                      ແມ່ແບບຄລາດສ໌ທີ່ເລືອກ
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {partnerClassNames.map((name, i) => (
                        <span
                          key={`c-${name}-${String(i)}`}
                          className="rounded bg-muted px-2 py-1 font-medium text-sm"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
                {partnerInstrNames.length > 0 ? (
                  <div>
                    <p className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                      ຜູ້ສອນ
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {partnerInstrNames.map((name, i) => (
                        <span
                          key={`i-${name}-${String(i)}`}
                          className="rounded border px-2 py-1 font-medium text-sm"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
                {search.startDate && search.expireDate ? (
                  <div>
                    <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                      ຊ່ວງເວລາທີ່ໃຊ້ໄດ້
                    </p>
                    <p className="font-semibold">
                      {formatDate(search.startDate)}{" "}
                      <ArrowRightIcon className="inline size-4" />{" "}
                      {formatDate(search.expireDate)}
                    </p>
                    {partnerValidDaysFromSale != null ? (
                      <p className="mt-1 text-muted-foreground text-xs">
                        ({partnerValidDaysFromSale} ວັນນັບຈາກວັນເລີ່ມຕາມການຂາຍ)
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {sessionsScheduledLegit ? (
                  <div>
                    <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                      ຈອງ ຄລາດສ໌ອັດຕະໂນມັດ
                    </p>
                    <p className="font-semibold tabular-nums">
                      ສ້າງ {partnerSessionsScheduled} ຄັ້ງ
                    </p>
                    <p className="mt-1 text-muted-foreground text-xs">
                      ກວດ ປະຕິທິນ ຄລາດສ໌ ສຳລັບລາຍລະອຽດ
                    </p>
                  </div>
                ) : null}
                {search.partnerSoldAt ? (
                  <div>
                    <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                      ວັນທີບັນທຶກ
                    </p>
                    <p className="font-semibold">
                      {formatDate(search.partnerSoldAt)}
                    </p>
                  </div>
                ) : null}
                {search.partnerPricingStatus ? (
                  <div className="border-t pt-4">
                    <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                      ສະຖານະລາຄາ
                    </p>
                    <p className="font-semibold text-sm uppercase">
                      {search.partnerPricingStatus}
                    </p>
                  </div>
                ) : null}
                <div className="border-t pt-4">
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                    ຮູບແບບການເງິນ
                  </p>
                  <p className="font-semibold text-sm">
                    {search.partnerPricingStatus === "draft" ||
                    search.partnerPricingStatus === "quoted"
                      ? "ຍັງບໍ່ເກັບເງິນ — ລໍຖ້າ lock"
                      : search.settlementMode === "credit"
                        ? "ໃບເກັບເງິນ / ຊຳລະບາງສ່ວນ"
                        : "ໃບເກັບເງິນເຕັມ"}
                  </p>
                </div>
                <div className="border-t pt-4">
                  <p className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                    ຈຳນວນເງິນຕາມການຂາຍ
                  </p>
                  {discountPartnerAmt != null &&
                    originalPartner != null &&
                    originalPartner !== totalPartner ? (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">ຍອດຕັ້ງ:</span>
                        <span>
                          {originalPartner.toLocaleString("lo-LA")} ກີບ
                        </span>
                      </div>
                      <div className="flex justify-between text-green-600 text-sm">
                        <span>
                          ສ່ວນຫຼຸດ
                          {search.discountType === "percentage" &&
                            search.discountValue
                            ? ` (${Number.parseFloat(search.discountValue)}%)`
                            : search.discountType === "fixed" &&
                              search.discountValue
                              ? ` (${Number.parseFloat(search.discountValue).toLocaleString("lo-LA")} ກີບ)`
                              : ""}
                          :
                        </span>
                        <span className="font-semibold">
                          -{discountPartnerAmt.toLocaleString("lo-LA")} ກີບ
                        </span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="font-semibold">ລາຄາສຸດທ້າຍ:</span>
                        <span className="font-bold text-3xl text-red-600">
                          {totalPartner.toLocaleString("lo-LA")} ກີບ
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="font-bold text-3xl text-red-600">
                      {totalPartner.toLocaleString("lo-LA")} ກີບ
                    </p>
                  )}
                </div>
              </div>
              {hasCreditInvoicePartner ? (
                <div className="rounded-lg border bg-muted/40 p-4 text-sm">
                  <p className="mb-2 font-semibold">ໃບເກັບເງິນ / ຊຳລະບາງສ່ວນ</p>
                  {creditSummaryPartner ? (
                    <div className="grid gap-2 md:grid-cols-2">
                      <div>
                        <p className="text-muted-foreground text-xs">ຮັບແລ້ວ</p>
                        <p className="font-medium tabular-nums">
                          {creditPaidPartner?.toLocaleString("lo-LA")} ກີບ
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">ຄ້າງ</p>
                        <p className="font-medium tabular-nums">
                          {creditRemainPartner?.toLocaleString("lo-LA")} ກີບ
                        </p>
                      </div>
                    </div>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    className={creditSummaryPartner ? "mt-3 w-full" : "w-full"}
                    onClick={() => {
                      nav({
                        to: "/app/billing/credit-invoices/$id",
                        params: { id: search.creditInvoiceId || "" },
                      });
                    }}
                  >
                    ເປີດໃບເກັບເງິນ
                  </Button>
                </div>
              ) : null}
              <div className="space-y-3 border-t pt-6">
                {search.partnerPricingStatus &&
                search.partnerPricingStatus !== "locked" &&
                search.partnerSaleId ? (
                  <Button
                    type="button"
                    className="w-full bg-red-600 text-white hover:bg-red-700"
                    onClick={() => {
                      nav({
                        to: "/app/sell/partner/$id/lock-pricing",
                        params: { id: search.partnerSaleId ?? "" },
                      });
                    }}
                  >
                    <ArrowRightIcon className="mr-2 size-4" />
                    Lock ລາຄາ & ເກັບເງິນ
                  </Button>
                ) : null}
                {search.receiptId ? (
                  <Button
                    type="button"
                    className="w-full bg-red-600 text-white hover:bg-red-700"
                    onClick={async () => {
                      const rid = search.receiptId;
                      if (!rid) return;
                      try {
                        await printReceiptById(rid);
                      } catch {
                        //
                      }
                    }}
                  >
                    <PrinterIcon className="mr-2 size-4" /> ພິມໃບເກັບເງິນ
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    nav({
                      to: "/app/sell",
                      search: { type: "partner-off-site" },
                    });
                  }}
                >
                  <PlusIcon className="size-4" /> ບັນທຶກໂຄງການ partner ອີກ
                </Button>
              </div>
            </div>
          </div>
        </Main>
      </>
    );
  }

  const handleGoToStudentProfile = () => {
    const sid = search.studentId;
    if (!sid) return;
    nav({
      to: "/app/students/$id",
      params: { id: sid },
    });
  };

  const handleSellAnother = () => {
    nav({
      to: "/app/sell",
      search: {
        type:
          search.type === "private-lesson-group" ||
            search.type === "private-lesson-shared"
            ? search.type
            : packageType,
      },
    });
  };

  const handleGoToBooking = () => {
    if (packageType !== "private-lesson") return;
    const sid = search.studentId;
    const pkg = search.packageId;
    if (!sid || !pkg) return;
    nav({
      to: "/app/private-lessons/bookings/bulk-create",
      search: {
        studentId: sid,
        packageId: pkg,
      },
    });
  };

  const privateSoldHours =
    packageType === "private-lesson" && search.privateLessonHours
      ? Number.parseInt(search.privateLessonHours, 10)
      : null;

  const privateSessionsScheduled =
    search.privateLessonSessionsScheduled != null &&
      search.privateLessonSessionsScheduled !== ""
      ? Number.parseInt(search.privateLessonSessionsScheduled, 10)
      : null;
  const privateSessionsScheduledLegit =
    privateSessionsScheduled != null &&
    Number.isFinite(privateSessionsScheduled) &&
    privateSessionsScheduled > 0;

  /** ຈຳນວນວັນທີ່ຂາຍ (ຈາກວັນເລີ່ມ → ວັນຫມົດອາຍຸ ຕາມທີ່ບັນທຶກ) */
  const privateValidDaysFromSale =
    packageType === "private-lesson" && search.startDate && search.expireDate
      ? (() => {
        const end = parseYmdLocal(search.expireDate);
        const start = parseYmdLocal(search.startDate);
        if (!end || !start) return null;
        const d = differenceInCalendarDays(end, start);
        return Number.isFinite(d) && d >= 0 ? d : null;
      })()
      : null;

  const privateHoursDisplay =
    packageType === "private-lesson" &&
      privateSoldHours != null &&
      !Number.isNaN(privateSoldHours)
      ? privateSoldHours
      : null;

  // Calculate total price from template if not provided
  const totalPrice =
    search.finalPrice && Number.parseFloat(search.finalPrice) > 0
      ? Number.parseFloat(search.finalPrice)
      : template
        ? packageType === "package"
          ? (template as PackageTemplateDTO).price
          : (privateSoldHours != null && !Number.isNaN(privateSoldHours)
            ? privateSoldHours
            : 1) * (template as PrivateLessonTemplateDTO).pricePerHour
        : 0;

  const originalPrice =
    search.originalPrice && Number.parseFloat(search.originalPrice) > 0
      ? Number.parseFloat(search.originalPrice)
      : template
        ? packageType === "package"
          ? (template as PackageTemplateDTO).price
          : (privateSoldHours != null && !Number.isNaN(privateSoldHours)
            ? privateSoldHours
            : 1) * (template as PrivateLessonTemplateDTO).pricePerHour
        : 0;

  const stackedPeriods = parseStackedPeriods(search.stackedPeriods);
  const stackedQuantity =
    search.quantity != null && search.quantity !== ""
      ? Number.parseInt(search.quantity, 10)
      : stackedPeriods.length;
  const isStackedPackageSell =
    packageType === "package" &&
    stackedPeriods.length > 1 &&
    !Number.isNaN(stackedQuantity) &&
    stackedQuantity > 1;

  const pageTitle =
    packageType === "package" ? "ຂາຍແພັກເກດສຳເລັດ" : "ຂາຍແພັກເກດຮຽນສ່ວນຕົວສຳເລັດ";

  const creditPaidNum =
    search.creditPaid != null && search.creditPaid !== ""
      ? Number.parseInt(search.creditPaid, 10)
      : null;
  const creditRemainingNum =
    search.creditRemaining != null && search.creditRemaining !== ""
      ? Number.parseInt(search.creditRemaining, 10)
      : null;
  const hasCreditInvoice = Boolean(search.creditInvoiceId);
  const showCreditSummary =
    hasCreditInvoice &&
    creditPaidNum != null &&
    !Number.isNaN(creditPaidNum) &&
    creditRemainingNum != null &&
    !Number.isNaN(creditRemainingNum);

  const creditPaidFinite =
    creditPaidNum != null && Number.isFinite(creditPaidNum);
  const creditRemainingFinite =
    creditRemainingNum != null && Number.isFinite(creditRemainingNum);
  const partialPaymentForReceipt = hasCreditInvoice
    ? {
      paidAmount: creditPaidFinite ? creditPaidNum : 0,
      remainingAmount: creditRemainingFinite
        ? creditRemainingNum
        : Math.max(0, totalPrice - (creditPaidFinite ? creditPaidNum : 0)),
      creditInvoiceId: search.creditInvoiceId,
    }
    : undefined;

  const canOpenPrintDialog =
    Boolean(search.receiptId) || Boolean(student && template);

  const handlePrintReceipt = async () => {
    try {
      if (search.receiptId) {
        await printReceiptById(search.receiptId);
        return;
      }
      if (student && template) {
        await printSellPackageReceipt({
          packageType,
          studentName: student.name,
          memberId: student.memberId,
          packageName: template.name,
          startDate: search.startDate ?? "",
          expireDate: search.expireDate ?? "",
          totalAmount: totalPrice,
          originalAmount: originalPrice,
          discountAmount: search.discountAmount
            ? Number.parseFloat(search.discountAmount)
            : 0,
          discountType:
            (search.discountType as "percentage" | "fixed") || undefined,
          discountValue: search.discountValue
            ? Number.parseFloat(search.discountValue)
            : undefined,
          packageId: search.packageId,
          totalHours:
            packageType === "private-lesson"
              ? privateSoldHours != null && !Number.isNaN(privateSoldHours)
                ? privateSoldHours
                : (template as PrivateLessonTemplateDTO).totalHours
              : undefined,
          pricePerHour:
            packageType === "private-lesson"
              ? (template as PrivateLessonTemplateDTO).pricePerHour
              : undefined,
          selectedClassNames:
            packageType === "package" && search.selectedClassNames
              ? search.selectedClassNames.split("|").filter(Boolean)
              : undefined,
          partialPayment: partialPaymentForReceipt,
        });
      }
    } catch {
      // network / print blocked
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຂາຍແພັກເກດ", to: "/app/sell" },
            { label: "ສຳເລັດ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mx-auto max-w-2xl">
          {/* Success Card */}
          <div className="rounded-xl border bg-card p-8 shadow-lg">
            {/* Success Indicator */}
            <div className="mb-6 flex flex-col items-center">
              <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <CheckCircleIcon className="size-8 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="mb-2 font-bold text-2xl">{pageTitle}</h2>
              <p className="text-center text-muted-foreground text-sm">
                {hasCreditInvoice
                  ? "ແພັກເກດເປີດໃຊ້ງານແລ້ວ · ຊຳລະຕໍ່ ແລະ ກວດສອບຍອດໄດ້ທີ່ໃບເກັບເງິນ"
                  : "ການທຸລະກຳຖືກປະມວນຜົນແລ້ວແລະແພັກເກດເປີດໃຊ້ງານແລ້ວ"}
              </p>
            </div>

            {/* Transaction Summary */}
            <div className="mb-6 space-y-6 border-t pt-6">
              <h3 className="font-semibold text-lg uppercase">ສະຫຼຸບການທຸລະກຳ</h3>

              {/* Student Information */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                    ຊື່ນັກຮຽນ
                  </p>
                  <p className="font-semibold">
                    {student?.name || "ກຳລັງໂຫຼດ..."}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                    ເລກສະມາຊິກ
                  </p>
                  <p className="font-semibold">
                    {student?.memberId || "ກຳລັງໂຫຼດ..."}
                  </p>
                </div>
              </div>

              {/* Package Information */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                    ຊື່ແພັກເກດ
                  </p>
                  <p className="font-semibold">
                    {template?.name || "ກຳລັງໂຫຼດ..."}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                    {isStackedPackageSell
                      ? `ຊ່ວງເວລາລວມ (${stackedQuantity} ຮອບ)`
                      : "ຊ່ວງເວລາທີ່ໃຊ້ໄດ້"}
                  </p>
                  <p className="font-semibold">
                    {formatDate(search.startDate)}{" "}
                    <ArrowRightIcon className="inline size-4" />{" "}
                    {formatDate(search.expireDate)}
                  </p>
                  {packageType === "private-lesson" &&
                    privateValidDaysFromSale != null && (
                      <p className="mt-1 text-muted-foreground text-xs">
                        ({privateValidDaysFromSale} ວັນນັບຈາກວັນເລີ່ມຕາມການຂາຍ)
                      </p>
                    )}
                  {isStackedPackageSell && search.purchaseBatchId ? (
                    <p className="mt-1 text-muted-foreground text-xs">
                      ຊຸດຂາຍ: {search.purchaseBatchId}
                    </p>
                  ) : null}
                </div>
              </div>

              {isStackedPackageSell && packageTemplate ? (
                <SellPackageStackedPeriodPreview
                  periods={stackedPeriods}
                  sessionsPerPeriod={packageTemplate.totalSessions}
                  pricePerPeriod={packageTemplate.price}
                  totalSessions={packageTemplate.totalSessions * stackedQuantity}
                  totalPrice={totalPrice}
                />
              ) : null}

              {/* Selected Classes (Package type with maxSelectableClasses) */}
              {packageType === "package" &&
                search.selectedClassNames &&
                search.selectedClassNames !== "" && (
                  <div>
                    <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                      ຄລາດສ໌ທີ່ເລືອກ
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {search.selectedClassNames.split("|").map((name) => (
                        <span
                          key={name}
                          className="rounded bg-muted px-2 py-1 font-medium text-sm"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              {/* Package Details (Private Lesson only) */}
              {packageType === "private-lesson" && template && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                      ຈຳນວນຊົ່ວໂມງທີ່ຊື້
                    </p>
                    <p className="font-semibold">
                      {privateHoursDisplay != null
                        ? `${privateHoursDisplay} ຊົ່ວໂມງ`
                        : "—"}
                    </p>
                    <p className="mt-1 text-muted-foreground text-xs">
                      ແມ່ແບບ = 1 ຊົ່ວໂມງຕໍ່ຫນ່ວຍລາຄາ
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                      ລາຄາຕໍ່ຊົ່ວໂມງ
                    </p>
                    <p className="font-semibold">
                      {(
                        template as PrivateLessonTemplateDTO
                      ).pricePerHour.toLocaleString("lo-LA")}{" "}
                      ກີບ
                    </p>
                  </div>
                  {privateValidDaysFromSale != null && (
                    <div>
                      <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                        ຈຳນວນວັນທີ່ກຳນົດຕອນຂາຍ
                      </p>
                      <p className="font-semibold">
                        {privateValidDaysFromSale} ວັນ
                      </p>
                    </div>
                  )}
                </div>
              )}

              {isPrivateLessonSell && privateSessionsScheduledLegit ? (
                <div>
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                    ການຈອງຕອນຂາຍ
                  </p>
                  <p className="font-semibold">
                    ສ້າງ {privateSessionsScheduled} ຄັ້ງ
                  </p>
                </div>
              ) : null}

              {search.creditInvoiceId ? (
                <div className="rounded-lg border bg-muted/40 p-4 text-sm">
                  <p className="mb-2 font-semibold">ໃບເກັບເງິນ / ຊຳລະບາງສ່ວນ</p>
                  {showCreditSummary ? (
                    <div className="grid gap-2 md:grid-cols-2">
                      <div>
                        <p className="text-muted-foreground text-xs">ຮັບແລ້ວ</p>
                        <p className="font-medium tabular-nums">
                          {creditPaidNum?.toLocaleString("lo-LA")} ກີບ
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">ຄ້າງ</p>
                        <p className="font-medium tabular-nums">
                          {creditRemainingNum?.toLocaleString("lo-LA")} ກີບ
                        </p>
                      </div>
                    </div>
                  ) : null}
                  <Button
                    onClick={() => {
                      nav({
                        to: "/app/billing/credit-invoices/$id",
                        params: { id: search.creditInvoiceId || "" },
                      });
                    }}
                    variant="outline"
                    className={showCreditSummary ? "mt-3 w-full" : "w-full"}
                  >
                    ເປີດໃບເກັບເງິນ
                  </Button>
                </div>
              ) : null}

              {/* Total Amount */}
              <div className="border-t pt-4">
                <p className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                  ຈຳນວນເງິນທີ່ຕ້ອງຊຳລະທັງໝົດ
                </p>
                {search.discountAmount &&
                  search.discountAmount !== "" &&
                  Number.parseFloat(search.discountAmount) > 0 ? (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">ຍອດເງິນ:</span>
                      <span>{originalPrice.toLocaleString("lo-LA")} ກີບ</span>
                    </div>
                    <div className="flex justify-between text-green-600 text-sm">
                      <span>
                        ສ່ວນຫຼຸດ
                        {search.discountType === "percentage" &&
                          search.discountValue
                          ? ` (${Number.parseFloat(search.discountValue)}%)`
                          : search.discountType === "fixed" &&
                            search.discountValue
                            ? ` (${Number.parseFloat(search.discountValue).toLocaleString("lo-LA")} ກີບ)`
                            : ""}
                        :
                      </span>
                      <span className="font-semibold">
                        -
                        {Number.parseFloat(
                          search.discountAmount,
                        ).toLocaleString("lo-LA")}{" "}
                        ກີບ
                      </span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-semibold">ລາຄາສຸດທ້າຍ:</span>
                      <span className="font-bold text-3xl text-red-600">
                        {totalPrice.toLocaleString("lo-LA")} ກີບ
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="font-bold text-3xl text-red-600">
                    {totalPrice.toLocaleString("lo-LA")} ກີບ
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 border-t pt-6">
              <div className="flex flex-col gap-3 sm:flex-row">
                {canOpenPrintDialog ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={handlePrintReceipt}
                  >
                    <PrinterIcon className="mr-2 size-4" />
                    {hasCreditInvoice ? "ພິມໃບເກັບເງິນ" : "ພິມໃບເກັບເງິນ"}
                  </Button>
                ) : null}
                {packageType === "private-lesson" && (
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={handleGoToBooking}
                  >
                    <CalendarIcon className="mr-2 size-4" />
                    ຈອງຫຼາຍຄັ້ງ
                  </Button>
                )}
                <Button
                  type="button"
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleGoToStudentProfile}
                >
                  ໄປທີ່ໂປຣໄຟລ໌ນັກຮຽນ
                  <ArrowRightIcon className="ml-2 size-4" />
                </Button>
              </div>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={handleSellAnother}
              >
                <PlusIcon className="size-4" />{" "}
                {packageType === "package"
                  ? "ຂາຍແພັກເກດອີກ"
                  : "ຂາຍແພັກເກດຮຽນສ່ວນຕົວອີກ"}
              </Button>
            </div>
          </div>
        </div>
      </Main>
    </>
  );
}

export { SellPackageSuccessPage };
export default SellPackageSuccessPage;
